/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren, ComponentProps } from 'preact';
import { MutableRef, useCallback, useEffect, useState } from 'preact/hooks';
import { Floating, PositionData } from '../UNSAFE_Floating';
import { Layer } from '../UNSAFE_Layer';
import { KEYS, isControlOrFunctionKey, preventDefaultForCurrentTarget } from './eventUtils';
import { dropdownStyles } from './themes/dropdownStyles.css';
import { useOutsideMousedown } from './useOutsideMousedown';

type FloatingProps = ComponentProps<typeof Floating>;

type Props = {
  anchorRef: MutableRef<HTMLElement | null>;
  children?: ComponentChildren;
  dropdownRef: MutableRef<HTMLElement | null>;
  id?: string;
  isOpen?: boolean;
  onAutoDismiss?: (event?: Event) => void;
  onPosition?: FloatingProps['onPosition'];
};

const DEFAULT_PLACEMENT = 'bottom-start';

export function Dropdown({
  anchorRef,
  children,
  dropdownRef,
  id,
  isOpen,
  onAutoDismiss,
  onPosition
}: Props) {
  const [placement, setPlacement] = useState<FloatingProps['placement']>(DEFAULT_PLACEMENT);

  useEffect(() => {
    // reset the placement to the default when the dropdown closes, not opens, so that it doesn't
    // initially open in the previous position and then move to the default position on the
    // rerender after the state is updated
    if (!isOpen) {
      setPlacement(DEFAULT_PLACEMENT);
    }
  }, [isOpen, setPlacement]);

  const handlePosition = useCallback(
    (positionData: PositionData) => {
      // update the placement so that the dropdown stays where it is until there is a collision,
      // i.e. it no longer fits, instead of letting it try to move back to the default position
      // on every render
      setPlacement(positionData.placement);

      // call the callback that was passed in through props
      onPosition?.(positionData);
    },
    [onPosition, setPlacement]
  );

  const handleAutoDismiss = useCallback(
    (event: Event) => {
      onAutoDismiss?.(event);
    },
    [onAutoDismiss]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.defaultPrevented || isControlOrFunctionKey(event)) {
        return;
      }

      switch (event.code) {
        case KEYS.ESC:
        case KEYS.TAB:
          handleAutoDismiss(event);
          break;
      }
    },
    [handleAutoDismiss]
  );

  // use the mousedown event to be consistent with legacy JET and so that we're hiding the dropdown
  // on the same event where focus will transfer
  useOutsideMousedown({
    isDisabled: !isOpen,
    ref: [anchorRef, dropdownRef],
    handler: handleAutoDismiss
  });

  // TODO: get dropdown y (mainAxis) offset from --oj-c-PRIVATE-DO-NOT-USE-private-core-global-dropdown-offset
  const offsetValue = { mainAxis: 4, crossAxis: 0 };

  const inlineStyle = isOpen
    ? {
        minWidth: `${anchorRef.current?.offsetWidth}px`
      }
    : {};

  return !isOpen ? null : (
    <Layer logicalParentRef={anchorRef}>
      <Floating
        anchorRef={anchorRef}
        sizeOptions={{
          isMaxHeightAdjusted: true,
          maxHeightCeiling: 400
        }}
        ref={dropdownRef}
        placement={placement}
        offsetValue={offsetValue}
        onPosition={handlePosition}>
        <div
          class={dropdownStyles.base}
          id={id}
          style={inlineStyle}
          onKeyDown={handleKeyDown}
          onMouseDown={preventDefaultForCurrentTarget}>
          {children}
        </div>
      </Floating>
    </Layer>
  );
}
